import * as React from 'react';
import { Grid } from 'ui';
import { Typography } from 'omakase';
import { core } from '@tock/coretypes';
import { truncate } from 'es-toolkit/compat';
import { useMediaQuery } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import ConsumerDisplayMoney from 'components/ConsumerDisplayMoney';
import ItemizedCard from 'components/business/menu/ItemizedCard';
import cssDefs from 'components/business/addons/supplementCompactCssDefs';

const CUSTOM_BREAKPOINT_SINGLE_COLUMN = 1040;

interface Props {
  id: number;
  collapse?: boolean;
  description: string;
  image?: core.IUploadedImage;
  disabled: boolean;
  isSoldOut: boolean;
  isLimitReached: boolean;
  modifierOptionGroups: core.ITicketOptionGroup[];
  name: string;
  price: number;
  quantity: number;
  onOpenModifierModal(
    ticketOptionId: number,
    modifierOptionGroups: core.ITicketOptionGroup[]
  ): void;
}

const SupplementCompact: React.FunctionComponent<Props> = ({
  id,
  collapse,
  description,
  image,
  disabled,
  isSoldOut,
  isLimitReached,
  modifierOptionGroups,
  name,
  price,
  quantity = 0,
  onOpenModifierModal,
}) => {
  const theme = useTheme();
  const handleOpenModifierModal = () => {
    onOpenModifierModal(id, modifierOptionGroups);
  };
  const shouldShowPrice = !isSoldOut || quantity > 0;

  /**
   * custom breakpoint for card single column. resolves TK-6741.
   * issue where item group limit display goes to two lines between 1040px and xs breakpoint.
   */
  const isWithinCustomBreakpoint = useMediaQuery(
    theme.breakpoints.between('xs', CUSTOM_BREAKPOINT_SINGLE_COLUMN)
  );

  return (
    <Grid item xs={12} md={isWithinCustomBreakpoint ? 12 : collapse ? 6 : undefined}>
      <ItemizedCard
        image={image ?? undefined}
        quantity={quantity}
        onClick={handleOpenModifierModal}
        disabled={disabled}
        data-testid="menu-item-card"
      >
        <div>
          <Typography
            component="h5"
            variant="body02Strong"
            color={disabled ? 'fgSubtle' : 'inherit'}
            css={cssDefs.name}
            data-testid="menu-item-name"
          >
            {name}
          </Typography>
          <Typography variant="caption" component="p" css={cssDefs.description}>
            {/* truncate description only kicks in on IE, where we cannot multiline truncate via webkit */}
            {truncate(description, {
              length: image ? 100 : 160,
              separator: /,? +/,
            })}
          </Typography>
        </div>
        <div css={cssDefs.contentFooter}>
          {shouldShowPrice ? (
            <Typography
              component="h6"
              variant="body02Strong"
              color={disabled ? 'fgSubtle' : 'inherit'}
            >
              {price > 0 && <ConsumerDisplayMoney amount={price} />}
            </Typography>
          ) : null}
          {isSoldOut ? (
            <Typography
              component="h6"
              variant="body02Strong"
              color="fgSubtle"
              css={shouldShowPrice ? cssDefs.spaceLeft : undefined}
            >
              Sold out
            </Typography>
          ) : isLimitReached ? (
            <Typography
              component="h6"
              variant="body02Strong"
              color="fgSubtle"
              css={shouldShowPrice ? cssDefs.spaceLeft : undefined}
            >
              Limit reached
            </Typography>
          ) : null}
        </div>
      </ItemizedCard>
    </Grid>
  );
};

export default SupplementCompact;
